import { PanelListDockingPanel } from "../docking-panels/panelListDockingPanel";
import { CornersModelEditor } from "../editors/cornersModelEditor";
import { CustomCornersModelEditor } from "../editors/customCornersModelEditor";
import { CustomPanelsModelEditor } from "../editors/customPanelsModelEditor";
import { CustomZShapedPanelsModelEditor } from "../editors/customZShapedPanelsModelEditor";
import { PanelsModelEditor } from "../editors/panelsModelEditor";
import { FacadeElement } from "../panels/common";
import { Corner } from "../panels/corner";
import { CustomCorner } from "../panels/customCorner";
import { CustomPanel } from "../panels/customPanel";
import { CustomZShapedPanel } from "../panels/customZShapedPanel";
import { FacadeElementType } from "../panels/facadeElementType";
import { Panel } from "../panels/panel";
import { hooksToolbarGroupId, measureToolsToolbarGroupId } from "../toolbar/toolbarGroupIds";
import { shallowEqual } from "../utils/shallowEqual";
import { compareAlphanumeric } from "../utils/sorting";
import { ViewerZoomingService } from "../viewer-utils/viewerZoomingService";
import {
    FitToViewPanelListEventPayload,
    PanelCategory,
    PanelListElement,
    PanelType,
    PanelListCommonElement,
} from "../eventBus/panelListEventPayloads";
import {
    CornerPanelSelectionChangedEventPayload,
    CustomCornerSelectionChangedEventPayload,
    CustomPanelSelectionChangedEventPayload,
    CustomZShapedPanelSelectionChangedEventPayload,
    PanelSelectionChangedEventPayload,
} from "../eventBus/selectionChangedEventsPayloads";
import eventBus, { IApplicationEvent } from "../eventBus/eventDispatcher";

const SQMM_TO_SQM_COEFFICIENT = 1e-6;

export type ForgePanelListExtensionLoadOptions = {
    panelsEditor: PanelsModelEditor;
    customPanelsEditor: CustomPanelsModelEditor;
    cornersEditor: CornersModelEditor;
    customCornersEditor: CustomCornersModelEditor;
    customZShapedPanelsModelEditor: CustomZShapedPanelsModelEditor;
    model: Autodesk.Viewing.Model;
};

export class ForgePanelListExtension extends Autodesk.Viewing.Extension {
    private panelList: PanelListElement[] = [];
    private dockingPanel: PanelListDockingPanel | null = null;
    private panelListButton: Autodesk.Viewing.UI.Button | null = null;
    private readonly panelsEditor: PanelsModelEditor;
    private readonly customPanelsEditor: CustomPanelsModelEditor;
    private readonly cornersEditor: CornersModelEditor;
    private readonly customCornersEditor: CustomCornersModelEditor;
    private readonly customZShapedPanelsModelEditor: CustomZShapedPanelsModelEditor;
    private readonly viewerZoomingService: ViewerZoomingService;
    private readonly model: Autodesk.Viewing.Model;

    constructor(viewer: Autodesk.Viewing.GuiViewer3D, options: ForgePanelListExtensionLoadOptions) {
        super(viewer, options);

        this.panelsEditor = options.panelsEditor;
        this.customPanelsEditor = options.customPanelsEditor;
        this.cornersEditor = options.cornersEditor;
        this.customCornersEditor = options.customCornersEditor;
        this.customZShapedPanelsModelEditor = options.customZShapedPanelsModelEditor;
        this.model = options.model;
        this.viewerZoomingService = new ViewerZoomingService(viewer, this.model);

        this.zoomToElement = this.zoomToElement.bind(this);
        this.onTogglePanelList = this.onTogglePanelList.bind(this);
        this.addNumber = this.addNumber.bind(this);
        this.createPanelListElement = this.createPanelListElement.bind(this);
        this.createPanelElement = this.createPanelElement.bind(this);
        this.createCustomPanelElement = this.createCustomPanelElement.bind(this);
        this.onDockingPanelVisibilityChanged = this.onDockingPanelVisibilityChanged.bind(this);

        this.updateEntities = this.updateEntities.bind(this);
        this.onElementSelectionChanged = this.onElementSelectionChanged.bind(this);
        this.onElementAddedRemoved = this.onElementAddedRemoved.bind(this);

        this.onEscape = this.onEscape.bind(this);
    }

    load() {
        this.viewer.addEventListener(Autodesk.Viewing.ESCAPE_EVENT, this.onEscape);

        eventBus.addEventListener("Dextall.PanelList.UI.PanelSelected", this.zoomToElement);

        eventBus.addEventListener("Dextall.Panels.SelectionChanged", this.onElementSelectionChanged);
        eventBus.addEventListener("Dextall.CustomPanels.SelectionChanged", this.onElementSelectionChanged);
        eventBus.addEventListener("Dextall.Corners.SelectionChanged", this.onElementSelectionChanged);
        eventBus.addEventListener("Dextall.CustomCorners.SelectionChanged", this.onElementSelectionChanged);
        eventBus.addEventListener("Dextall.CustomZShapedPanels.SelectionChanged", this.onElementSelectionChanged);

        eventBus.addEventListener("Dextall.CustomPanels.Created", this.onElementAddedRemoved);
        eventBus.addEventListener("Dextall.CustomPanels.Removed", this.onElementAddedRemoved);
        eventBus.addEventListener("Dextall.CustomCorners.Created", this.onElementAddedRemoved);
        eventBus.addEventListener("Dextall.CustomCorners.Removed", this.onElementAddedRemoved);
        eventBus.addEventListener("Dextall.CustomZShapedPanels.Created", this.onElementAddedRemoved);
        eventBus.addEventListener("Dextall.CustomZShapedPanels.Removed", this.onElementAddedRemoved);

        return true;
    }

    unload() {
        this.viewer.removeEventListener(Autodesk.Viewing.ESCAPE_EVENT, this.onEscape);

        eventBus.removeEventListener("Dextall.PanelList.UI.PanelSelected", this.zoomToElement);

        eventBus.removeEventListener("Dextall.Panels.SelectionChanged", this.onElementSelectionChanged);
        eventBus.removeEventListener("Dextall.CustomPanels.SelectionChanged", this.onElementSelectionChanged);
        eventBus.removeEventListener("Dextall.Corners.SelectionChanged", this.onElementSelectionChanged);
        eventBus.removeEventListener("Dextall.CustomCorners.SelectionChanged", this.onElementSelectionChanged);
        eventBus.removeEventListener("Dextall.CustomZShapedPanels.SelectionChanged", this.onElementSelectionChanged);

        eventBus.removeEventListener("Dextall.CustomPanels.Created", this.onElementAddedRemoved);
        eventBus.removeEventListener("Dextall.CustomPanels.Removed", this.onElementAddedRemoved);
        eventBus.removeEventListener("Dextall.CustomCorners.Created", this.onElementAddedRemoved);
        eventBus.removeEventListener("Dextall.CustomCorners.Removed", this.onElementAddedRemoved);
        eventBus.removeEventListener("Dextall.CustomZShapedPanels.Created", this.onElementAddedRemoved);
        eventBus.removeEventListener("Dextall.CustomZShapedPanels.Removed", this.onElementAddedRemoved);

        this.dockingPanel?.shutdown();

        this.panelListButton?.removeEventListener("click", this.onTogglePanelList);

        return true;
    }

    onToolbarCreated() {
        const toolbar = this.viewer.toolbar;

        let group = toolbar.getControl(hooksToolbarGroupId) as Autodesk.Viewing.UI.ControlGroup | undefined;

        if (!group) {
            const measureTools = toolbar.getControl(measureToolsToolbarGroupId) as
                | Autodesk.Viewing.UI.ControlGroup
                | undefined;

            if (!measureTools) {
                toolbar.addControl(new Autodesk.Viewing.UI.ControlGroup(measureToolsToolbarGroupId), { index: 1 });
            }

            group = new Autodesk.Viewing.UI.ControlGroup(hooksToolbarGroupId);
            toolbar.addControl(group, { index: toolbar.indexOf(measureToolsToolbarGroupId) });
        }

        this.panelListButton = new Autodesk.Viewing.UI.Button("dextall-panel-list-button");
        this.panelListButton.setToolTip("Panel List");
        this.panelListButton.setIcon("viewer-editor-panel-list");

        this.panelListButton.addEventListener("click", this.onTogglePanelList);

        group.addControl(this.panelListButton);

        this.dockingPanel = new PanelListDockingPanel(this.viewer.container, this.onDockingPanelVisibilityChanged);
    }

    private onTogglePanelList() {
        const newState = !this.dockingPanel?.visible;

        this.dockingPanel?.setVisible(newState);
    }

    private zoomToElement(event: IApplicationEvent<FitToViewPanelListEventPayload>) {
        const { id, category } = event.payload;

        switch (category) {
            case PanelCategory.Panel: {
                const panel = this.panelsEditor.findPanelById(id);
                panel && this.zoomToPanel(panel);

                return;
            }
            case PanelCategory.CustomPanel: {
                const customPanel = this.customPanelsEditor.findPanelById(id);
                customPanel && this.zoomToPanel(customPanel);

                return;
            }
            case PanelCategory.Corner: {
                const corner = this.cornersEditor.findCornerById(id);
                corner && this.zoomToPanel(corner);

                return;
            }
            case PanelCategory.CustomCorner: {
                const customCorner = this.customCornersEditor.findCustomCornerById(id);
                customCorner && this.zoomToPanel(customCorner);

                return;
            }
            case PanelCategory.CustomZShapedPanel: {
                const customZShapedPanel = this.customZShapedPanelsModelEditor.findCustomPanelById(id);
                customZShapedPanel && this.zoomToPanel(customZShapedPanel);

                return;
            }
        }
    }

    private zoomToPanel(panel: FacadeElement) {
        this.viewerZoomingService.zoomToPanel(panel);
    }

    private onDockingPanelVisibilityChanged(visibility: boolean) {
        const newState = visibility
            ? Autodesk.Viewing.UI.Button.State.ACTIVE
            : Autodesk.Viewing.UI.Button.State.INACTIVE;

        if (visibility) {
            this.createPanelList();
        }

        this.panelListButton?.setState(newState);
    }

    private onEscape() {
        this.dockingPanel?.setVisible(false);
    }

    private createPanelList() {
        const panels = this.panelsEditor.panels;
        const customPanels = this.customPanelsEditor.customPanels;
        const corners = this.cornersEditor.corners;
        const customCorners = this.customCornersEditor.customCorners;
        const customZShapedPanels = this.customZShapedPanelsModelEditor.customZShapedPanels;

        this.panelList = [...panels, ...customPanels, ...corners, ...customCorners, ...customZShapedPanels]
            .map(this.createPanelListElement)
            .sort((a, b) => compareAlphanumeric(a.panelId.toLowerCase(), b.panelId.toLowerCase()))
            .map(this.addNumber);

        eventBus.dispatchEvent({
            type: "Dextall.PanelList.PanelListCreated",
            payload: this.panelList,
        });
    }

    private addNumber(element: PanelListElement, index: number): PanelListElement {
        return {
            ...element,
            number: index + 1,
        };
    }

    private createPanelListElement(originalFacadeElement: FacadeElement): PanelListElement {
        const heightMm = Autodesk.Viewing.Private.convertUnits("ft", "mm", 1, originalFacadeElement.height);
        const depthMm =
            originalFacadeElement.depth === null
                ? null
                : Autodesk.Viewing.Private.convertUnits("ft", "mm", 1, originalFacadeElement.depth);

        const panelListCommonElement: PanelListCommonElement = {
            id: originalFacadeElement.id,
            installationSequence: originalFacadeElement.installationSequence,
            containerNumber: "-", // will be implemented later
            heightMm,
            floor: "-", // will be implemented later
            windowDoorType: "-", // will be implemented later
            weightKg: null, // will be implemented later
            weightLbs: null, // will be implemented later
            depthMm,
        };

        if (originalFacadeElement.elementType === FacadeElementType.CustomPanel) {
            return this.createCustomPanelElement(panelListCommonElement, originalFacadeElement);
        } else if (originalFacadeElement.elementType === FacadeElementType.StandardCorner) {
            return this.createCornerElement(panelListCommonElement, originalFacadeElement);
        } else if (originalFacadeElement.elementType === FacadeElementType.CustomCorner) {
            return this.createCustomCornerElement(panelListCommonElement, originalFacadeElement);
        } else if (originalFacadeElement.elementType === FacadeElementType.CustomZShaped) {
            return this.createCustomZShapedPanelElement(panelListCommonElement, originalFacadeElement);
        }

        return this.createPanelElement(panelListCommonElement, originalFacadeElement);
    }

    private createPanelElement(commonElement: PanelListCommonElement, element: Panel): PanelListElement {
        const widthMm = Autodesk.Viewing.Private.convertUnits("ft", "mm", 1, element.length);
        const areaM2 = commonElement.heightMm * widthMm * SQMM_TO_SQM_COEFFICIENT;
        const panelType = element.customPanelTypeId
            ? this.customPanelsEditor.findCustomPanelType(element.customPanelTypeId)?.name
            : this.panelsEditor
                .findPanelFamily(element.panelTypeId)
                ?.panelTypes.find(panelFamily => panelFamily.id === element.panelTypeId)?.name;

        return {
            ...commonElement,
            panelType,
            panelId: element.elementName?.trim() ?? "",
            type: element.hasWindows() ? PanelType.Window : PanelType.Straight,
            category: PanelCategory.Panel,
            side: element.facadeOrientation,
            widthMm,
            areaM2,
        };
    }

    private createCustomPanelElement(commonElement: PanelListCommonElement, element: CustomPanel): PanelListElement {
        const widthMm = Autodesk.Viewing.Private.convertUnits("ft", "mm", 1, element.length);
        const areaM2 = commonElement.heightMm * widthMm * SQMM_TO_SQM_COEFFICIENT;
        const panelType = this.customPanelsEditor
            .findValidCustomPanelTypesForCustomPanel(element.id)
            .find(customPanelType => customPanelType.id === element.customPanelTypeId)?.name;

        return {
            ...commonElement,
            panelType,
            panelId: element.elementName?.trim() ?? "",
            type: PanelType.Atypical,
            category: PanelCategory.CustomPanel,
            side: element.facadeOrientation,
            widthMm,
            areaM2,
        };
    }

    private createCornerElement(commonElement: PanelListCommonElement, element: Corner): PanelListElement {
        const widthLMm = Autodesk.Viewing.Private.convertUnits("ft", "mm", 1, element.leftWing);
        const widthRMm = Autodesk.Viewing.Private.convertUnits("ft", "mm", 1, element.rightWing);
        const areaM2 = commonElement.heightMm * (widthLMm + widthRMm) * SQMM_TO_SQM_COEFFICIENT;
        const panelType = element.customPanelTypeId
            ? this.customCornersEditor.findCustomCornerType(element.customPanelTypeId)?.name
            : this.cornersEditor
                .findPanelFamily(element.panelTypeId)
                ?.panelTypes.find(panelFamily => panelFamily.id === element.panelTypeId)?.name;

        return {
            ...commonElement,
            panelType,
            panelId: element.elementName?.trim() ?? "",
            type: PanelType.Corner,
            category: PanelCategory.Corner,
            side: `${element.leftWingFacadeOrientation}/${element.rightWingFacadeOrientation}`,
            widthLMm,
            widthRMm,
            areaM2,
        };
    }

    private createCustomCornerElement(commonElement: PanelListCommonElement, element: CustomCorner): PanelListElement {
        const widthLMm = Autodesk.Viewing.Private.convertUnits("ft", "mm", 1, element.leftWing);
        const widthRMm = Autodesk.Viewing.Private.convertUnits("ft", "mm", 1, element.rightWing);
        const areaM2 = commonElement.heightMm * (widthLMm + widthRMm) * SQMM_TO_SQM_COEFFICIENT;
        const panelType = this.customCornersEditor
            .findValidCustomCornerTypesForPanel(element.id)
            .find(customPanelType => customPanelType.id === element.customPanelTypeId)?.name;

        return {
            ...commonElement,
            panelType,
            panelId: element.elementName?.trim() ?? "",
            type: PanelType.Atypical,
            category: PanelCategory.CustomCorner,
            side: `${element.leftWingFacadeOrientation}/${element.rightWingFacadeOrientation}`,
            widthLMm,
            widthRMm,
            areaM2,
        };
    }

    private createCustomZShapedPanelElement(
        commonElement: PanelListCommonElement,
        element: CustomZShapedPanel,
    ): PanelListElement {
        const widthMm = Autodesk.Viewing.Private.convertUnits("ft", "mm", 1, element.shelf);
        const widthLMm = Autodesk.Viewing.Private.convertUnits("ft", "mm", 1, element.leftWing);
        const widthRMm = Autodesk.Viewing.Private.convertUnits("ft", "mm", 1, element.rightWing);
        const areaM2 = commonElement.heightMm * (widthMm + widthLMm + widthRMm) * SQMM_TO_SQM_COEFFICIENT;
        const panelType = this.customZShapedPanelsModelEditor.findCustomPanelType(element.customPanelTypeId)?.name;

        return {
            ...commonElement,
            panelType,
            panelId: element.elementName?.trim() ?? "",
            type: PanelType.Atypical,
            category: PanelCategory.CustomZShapedPanel,
            side: `${element.leftWingFacadeOrientation}/${element.shelfFacadeOrientation}/${element.rightWingFacadeOrientation}`,
            widthMm,
            widthLMm,
            widthRMm,
            areaM2,
        };
    }

    private updateEntities() {
        eventBus.dispatchEvent({
            type: "Dextall.PanelList.EntitiesUpdated",
            payload: this.panelList,
        });
    }

    private onElementSelectionChanged(
        event:
            | IApplicationEvent<PanelSelectionChangedEventPayload | null>
            | IApplicationEvent<CustomPanelSelectionChangedEventPayload | null>
            | IApplicationEvent<CornerPanelSelectionChangedEventPayload | null>
            | IApplicationEvent<CustomCornerSelectionChangedEventPayload | null>
            | IApplicationEvent<CustomZShapedPanelSelectionChangedEventPayload | null>,
    ) {
        const payloadElement = event.payload?.panel;
        let shouldUpdate = false;

        if (this.dockingPanel?.visible && payloadElement?.id) {
            this.panelList = this.panelList.map(element => {
                if (element.id !== payloadElement.id) {
                    return element;
                } else {
                    const updatedElement: PanelListElement = {
                        ...element,
                        ...this.createPanelListElement(payloadElement),
                    };

                    shouldUpdate = !shallowEqual(updatedElement, element);

                    return updatedElement;
                }
            });
            shouldUpdate && this.updateEntities();
            shouldUpdate = false;
        }
    }

    private onElementAddedRemoved() {
        if (this.dockingPanel?.visible) {
            this.createPanelList();
        }
    }
}

export const forgePanelListExtensionName = "Dextall.ForgePanelListExtension" as const;

Autodesk.Viewing.theExtensionManager.registerExtension(forgePanelListExtensionName, ForgePanelListExtension);
